/**
 * 📅 일정 — 네이버웍스 회사 캘린더를 포털에서 보고 **직접 넣고 고치는** 화면 (v2.139.0 · 2026-09-08)
 *
 * 사용자 지시(2026-09-08): 「캘린더를 지금처럼 대시보드 내부에 말고 **왼쪽 메뉴 중 하나로 제대로**
 *   넣어 줘. **입력 편집도 할 수 있게.**」
 *   → 대시보드 안의 작은 달력(v2.138.0 `WorksCalendarPanel`)은 걷어내고 이 화면으로 옮겼다.
 *
 * 화면 구성
 *   왼쪽  월 달력 — 날짜를 누르면 오른쪽에 그 날 일정이 펼쳐진다.
 *   오른쪽 그 날의 일정 목록 + [＋ 이 날에 일정 추가]. 일정마다 [수정]·[삭제]·[웍스에서 열기].
 *
 * 색과 자물쇠
 *   · **주황 = 영업진행이 만든 납품 일정**. 여기서는 **못 고친다** — 영업건의 납품예정일에서 자동으로
 *     만들어지므로, 여기서 고쳐도 영업건을 다음에 저장하면 되돌아간다. 그 이유를 화면에 적는다.
 *   · **빨강 = 연차·반차 등 휴가**(제목 맨 앞 대괄호에 연차·반차·휴가가 있으면 · v2.182.0).
 *   · **파랑 = 사람이 넣은 일정**(포털에서 넣었든 웍스 앱에서 넣었든). 권한이 있으면 고칠 수 있다.
 *   ⭐색은 보기 편하라고 붙이는 것일 뿐, **잠글지 말지는 서버가 번호로 판정한다**(제목 글자 아님).
 *     휴가 빨강만 제목 글자로 고른다 — **색에만 쓰고 잠금에는 절대 쓰지 않는다**(`isLeave` 주석).
 *
 * ⭐**등록·수정·삭제는 로그인한 전 직원**(viewer 포함 · v2.186.0 · 사용자 지시). 회사 공용 캘린더이고
 *   viewer 도 웍스 앱에서는 원래 직접 넣고 고칠 수 있어서, 포털에서만 막을 이유가 없다.
 *   ⚠단추를 보일지는 **서버가 준 값**(`canCreate` · 일정마다 `canEdit`·`lockReason`)만 따른다 —
 *     화면이 따로 역할을 판정하면 두 벌 규칙이 되어 언젠가 어긋난다.
 *
 * ⭐크기 (v2.139.1 · 사용자 요청 「캘린더 크기를 좀 더 키워 줘. 글자크기도. **특히 세로 길이**」)
 *   칸 높이 92/108px → **132/168px**, 날짜·요일 11px → **14px**, 일정 칩 10px → **12px**,
 *   한 칸에 보이는 일정 3건 → **4건**, 오른쪽 패널은 **폭 340px 그대로**(넓히면 달력 칸이 좁아진다)·글자만 한 단계씩.
 *   ⚠더 키우려면 **칸 높이부터** 올릴 것 — 글자만 키우면 막대가 칸을 넘쳐 다음 주 줄로 흘러내린다.
 *   ⭐v2.183.0 — 폰에도 **글자를 보여 준다**(전에는 색 막대만). 막대가 16px → 27px 로 두꺼워져
 *     칸 높이를 **그 주가 쓴 줄 수만큼 계산**해서 잡는다(`CELL_MIN_H`·`LANE_H`). 폰 기본 칸도 168px.
 */

import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HOLIDAY_LAST_YEAR, holidayOf, holidayTableEndingSoon, holidayTableMissing } from '../lib/holidays';
import {
  listWorksEvents,
  createWorksEvent,
  updateWorksEvent,
  deleteWorksEvent,
  type WorksEvent,
  type WorksEventInput,
} from '../lib/calendar-api';
import { DateInput } from '../components/DateInput';
import { isStandalone, useInstallAs, useInstallPrompt } from '../lib/install-as';
import {
  MAX_LANES, compareForCell, isDelivery, isLeave, layoutWeek, monthGrid, toWeeks,
} from '../lib/calendar-layout';
import { LoadingLines } from '../components/ui/Skeleton';
import { toastOk, toastWarn, toastError } from '../lib/toast';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 달력 일곱 칸의 폭 — **폰에서는 토·일을 절반으로 줄이고 그만큼 평일을 고르게 넓힌다**
 * (v2.183.1 · 사용자 요청 2026-09-16 「토요일과 일요일은 가로 폭을 1/2 로 줄이자.
 *  그만큼 다른 요일들의 폭을 균일하게 넓혀주고」).
 *
 * `1 2 2 2 2 2 1` = 열두 몫. 평일이 2/12(=1/6) 씩, 주말이 1/12 씩 —
 * 평일 칸이 1/7 → 1/6 으로 **약 17% 넓어진다.** 큰 화면은 종전대로 일곱 칸이 똑같다.
 *
 * ⚠⚠**세 격자(요일 머리줄 · 날짜 칸 · 일정 막대)가 반드시 같은 값이어야 한다** —
 *   하나라도 어긋나면 막대가 엉뚱한 날에 그어진다. 그래서 한 곳에 두고 셋이 같이 쓴다.
 * ⚠Tailwind 는 **글자 그대로 적힌 클래스만** 만들어 준다 — 조각을 이어 붙이지 말 것.
 *
 * ⛔⛔**`1fr` 이 아니라 `minmax(0,1fr)` 이어야 한다.** `1fr` 은 `minmax(auto,1fr)` 이라
 *   **내용이 길면 칸이 늘어난다.** 달력은 **주마다 격자가 따로**라, 그러면 「개천절 대체공휴일」이
 *   들어 있는 주만 칸 폭이 달라져 **주끼리 세로줄이 어긋난다**(처음에 `1fr` 로 썼다가 화면에서 잡았다).
 *   Tailwind 의 `grid-cols-7` 이 `repeat(7,minmax(0,1fr))` 인 것도 같은 이유다.
 */
const COLS =
  'grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)] sm:grid-cols-7';

/**
 * 막대가 시작하는 높이(px) = 칸 안쪽 여백(`p-1.5` 6px) + 날짜 머리줄(`h-7` 28px) + 숨 2px.
 * ⚠⚠**머리줄을 `h-7` 로 못 박아 두었기 때문에** 이 한 값이 모든 칸에 똑같이 맞는다 —
 *   오늘 날짜만 동그라미(28px)라, 높이를 자유롭게 두면 그 주의 막대가 통째로 밀린다.
 */
const WEEK_BAR_TOP = 36;

/**
 * 칸 높이 계산에 쓰는 실측값 (px).
 *
 * ⚠⚠**막대는 칸 위에 겹쳐 그리므로 칸을 밀어내지 못한다** — 칸이 모자라면 막대가
 *   **다음 주 줄로 흘러내린다.** 그래서 그 주에 쓴 줄 수만큼 칸 높이를 **미리** 잡아 준다.
 *   (v2.183.0 에서 폰에도 글자를 보이게 하면서 막대가 16px → **27px** 로 두꺼워진 자리다.)
 * ⚠이 값들은 막대의 css(`text-xs` · `py-1` · 테두리 · `gap-y-0.5`)에서 나온 **실측**이다.
 *   막대 모양을 건드리면 여기도 함께 재야 한다.
 */
const LANE_H = 27;
const LANE_GAP = 2;
const MORE_H = 20;
/** 일정이 없는 주의 기본 칸 높이 — 폰·큰 화면 같은 값 (전에는 폰만 132px 이었다) */
const CELL_MIN_H = 168;

/** 'YYYY-MM-DD' — 시간대에 휘둘리지 않게 숫자로 만든다 */
function ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function todayYmd(): string {
  const t = new Date();
  return ymd(t.getFullYear(), t.getMonth(), t.getDate());
}
/** '2026-09-10' → '9월 10일 (수)' */
function labelOf(day: string): string {
  const t = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(t)) return day;
  const d = new Date(t);
  return `${d.getUTCMonth() + 1}월 ${d.getUTCDate()}일 (${WEEKDAYS[d.getUTCDay()]})`;
}

/* 색·차례 규칙과 막대 자리 계산은 `lib/calendar-layout.ts` 한 곳에 있다 (화면 없이 검사하려고). */

type FormState = {
  eventId: string | null;
  summary: string;
  date: string;
  endDate: string;
  allDay: boolean;
  startTime: string;
  endTime: string;
  location: string;
  description: string;
};

function emptyForm(day: string): FormState {
  return {
    eventId: null,
    summary: '',
    date: day,
    endDate: day,
    allDay: true,
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    description: '',
  };
}

function formOf(e: WorksEvent): FormState {
  return {
    eventId: e.eventId,
    summary: e.summary,
    date: e.date,
    endDate: e.endDate >= e.date ? e.endDate : e.date,
    allDay: e.allDay,
    startTime: e.startTime ?? '09:00',
    endTime: e.endTime ?? '10:00',
    location: e.location ?? '',
    description: e.description ?? '',
  };
}

export function CalendarPage(): React.JSX.Element {
  const now = new Date();
  const qc = useQueryClient();
  const [ym, setYm] = useState<{ y: number; m: number }>({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState<string>(todayYmd());
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string>('');

  // 달력 한 장 — 앞뒤 달 칸까지 진짜 날짜(v2.277.0 · 전엔 빈칸). 일정도 그 칸들까지 불러온다(최대 42일 · 서버가 31일씩 나눠 웍스에 묻는다)
  const grid = useMemo(() => monthGrid(ym.y, ym.m), [ym.y, ym.m]);

  const q = useQuery({
    queryKey: ['works-calendar', grid.from, grid.to],
    queryFn: () => listWorksEvents(grid.from, grid.to),
    staleTime: 30_000,
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['works-calendar'] });
  };

  const createM = useMutation({
    mutationFn: (input: WorksEventInput) => createWorksEvent(input),
    onSuccess: () => {
      setForm(null);
      setFormError('');
      toastOk('일정을 넣었습니다');
      invalidate();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const updateM = useMutation({
    mutationFn: (v: { eventId: string; input: WorksEventInput }) => updateWorksEvent(v.eventId, v.input),
    onSuccess: (res) => {
      setForm(null);
      setFormError('');
      if (res.warning) toastWarn(res.warning);
      else toastOk('일정을 고쳤습니다');
      invalidate();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteM = useMutation({
    mutationFn: (eventId: string) => deleteWorksEvent(eventId),
    onSuccess: () => {
      toastOk('일정을 지웠습니다');
      invalidate();
    },
    onError: (e: Error) => toastError(`지우지 못했습니다 — ${e.message}`),
  });

  /** 날짜별로 일정을 담는다 — 여러 날 걸치는 일정은 걸친 날마다 넣는다 */
  const byDate = useMemo(() => {
    const map = new Map<string, WorksEvent[]>();
    for (const e of q.data?.events ?? []) {
      const from = e.date;
      const to = e.endDate >= e.date ? e.endDate : e.date;
      for (let t = Date.parse(`${from}T00:00:00Z`); t <= Date.parse(`${to}T00:00:00Z`); t += 86400000) {
        const key = new Date(t).toISOString().slice(0, 10);
        const arr = map.get(key);
        if (arr) arr.push(e);
        else map.set(key, [e]);
      }
    }
    return map;
  }, [q.data]);


  /**
   * 달력 칸(주 단위) + 그 주의 막대 자리.
   *
   * ⭐**우선순위대로 줄을 세워 넘긴다** — `layoutWeek` 은 먼저 온 일정에게 위 줄을 준다.
   *   그래서 연차·휴가가 맨 위, 그다음 [납품], 그다음 시간 순이 된다(`compareForCell`).
   * ⭐**여러 날 걸치는 일정은 주마다 한 번만** 막대가 된다 — 날마다 따로 그리지 않는다.
   */
  const { weeks, weekLayouts } = useMemo(() => {
    const keys: (string | null)[] = grid.keys;

    const ordered = (q.data?.events ?? [])
      .map((e, i) => ({ e, i }))
      .sort(compareForCell)
      .map((x) => x.e);

    const ws = toWeeks(keys);
    return {
      weeks: ws,
      weekLayouts: ws.map((w) =>
        layoutWeek(w, ordered, (e) => ({ start: e.date, end: e.endDate }), MAX_LANES),
      ),
    };
  }, [q.data, grid]);

  /** 이 달(1일~말일)에 하루라도 걸친 일정 수 */
  const monthEventCount = useMemo(() => {
    const mFirst = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-01`;
    const mLast = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-${String(new Date(ym.y, ym.m + 1, 0).getDate()).padStart(2, '0')}`;
    return (q.data?.events ?? []).filter((e) => e.date <= mLast && (e.endDate >= e.date ? e.endDate : e.date) >= mFirst).length;
  }, [q.data, ym.y, ym.m]);

  const today = todayYmd();
  const isThisMonth = ym.y === now.getFullYear() && ym.m === now.getMonth();
  /**
   * 달 넘기기. ⭐고른 날도 그 달 안으로 옮긴다 — 안 옮기면 「2026년 10월」을 보면서 오른쪽 패널은
   * 「9월 8일」을 보여 준다(실제로 그렇게 나왔다). 그 달에 오늘이 있으면 오늘, 없으면 1일.
   */
  const move = (delta: number): void => {
    const d = new Date(ym.y, ym.m + delta, 1);
    const y = d.getFullYear();
    const m = d.getMonth();
    setYm({ y, m });
    setSelected(y === now.getFullYear() && m === now.getMonth() ? today : ymd(y, m, 1));
  };
  const goToday = (): void => {
    setYm({ y: now.getFullYear(), m: now.getMonth() });
    setSelected(today);
  };

  /**
   * ⭐**아이폰용 아이콘·이름만** 바꾼다 (v2.188.0). 이 화면에서 「홈 화면에 추가」를 하면
   *   달력 아이콘·「SM 일정」 이름이 붙는다(사파리는 매니페스트를 안 보고 이 태그들을 본다).
   *   화면을 떠나면 포털 기본값으로 되돌아간다.
   *   ⛔**안드로이드용 두 번째 앱은 없다** — 한 주소에 앱 하나뿐이라 원리적으로 안 된다.
   *   자세한 사정은 `lib/install-as.ts` · `components/MobileAppTabs.tsx`.
   */
  useInstallAs('calendar');
  /** 이미 앱으로 열렸으면 설치 안내를 띄우지 않는다 — 한 번 보고 나면 잔소리가 된다 */
  const [showInstallTip] = useState(() => !isStandalone());
  /** 사이드바 없는 앱 껍데기(`/m/calendar`) 안인가 — 안내 문구가 달라진다 */
  const inAppShell = useLocation().pathname.startsWith('/m/');
  /** 브라우저가 「앱으로 깔 수 있다」고 한 경우에만 진짜 설치 단추를 준다 */
  const { canInstall, install } = useInstallPrompt();

  const canCreate = q.data?.canCreate ?? false;
  const dayList = byDate.get(selected) ?? [];
  const saving = createM.isPending || updateM.isPending;

  const openNew = (day: string): void => {
    setFormError('');
    setForm(emptyForm(day));
  };
  const openEdit = (e: WorksEvent): void => {
    setFormError('');
    setForm(formOf(e));
  };

  const submit = (): void => {
    if (!form) return;
    const input: WorksEventInput = {
      summary: form.summary,
      date: form.date,
      endDate: form.endDate || form.date,
      allDay: form.allDay,
      startTime: form.allDay ? null : form.startTime,
      endTime: form.allDay ? null : form.endTime,
      location: form.location,
      description: form.description,
    };
    if (!input.summary.trim()) {
      setFormError('제목을 적어 주세요');
      return;
    }
    if (form.eventId) updateM.mutate({ eventId: form.eventId, input });
    else createM.mutate(input);
  };

  const askDelete = (e: WorksEvent): void => {
    const where =
      e.source === 'works'
        ? '\n\n이 일정은 웍스에서 직접 만든 일정입니다. 지우면 웍스 캘린더에서도 사라집니다.'
        : '\n\n지우면 웍스 캘린더에서도 사라집니다.';
    if (!window.confirm(`「${e.summary}」 일정을 지울까요?${where}`)) return;
    deleteM.mutate(e.eventId);
  };

  /* ── 캘린더 연동 자체가 없는 서버 ── */
  if (q.data && !q.data.enabled) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="ui-page-title mb-3">일정</h1>
        <div className="bg-white rounded shadow-sm border border-slate-200 p-6 text-sm text-slate-500">
          웍스 캘린더 연동이 설정되지 않은 서버입니다. 관리자에게 문의해 주세요.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h1 className="ui-page-title">
          일정{' '}
          <span className="text-sm font-normal text-slate-400">네이버웍스 회사 캘린더</span>
        </h1>
        {/* ⭐⭐⭐**안내는 세 갈래다** (v2.188.0 · 앱은 하나로 정리한 뒤).
            ① 브라우저가 설치 신호를 줬다 → 단추. 깔리는 것은 **포털 앱 하나**(📦 재고 · 📅 일정 두 칸).
            ② 신호가 없고 사이드바 쪽이다 → 아이폰용 두 걸음 안내(앱 화면에서 추가해야 사이드바가 안 딸려 온다).
            ③ 신호가 없고 앱 화면이다 → **왜 없는지**를 솔직히 적는다(아래 주석).
            ⭐폰에서만(`sm:hidden`), 그리고 **아직 앱으로 안 열었을 때만**(`showInstallTip`) 보인다. */}
        {showInstallTip && canInstall && (
          <div className="sm:hidden w-full">
            <button
              type="button"
              onClick={install}
              className="btn btn-primary w-full"
            >
              📲 앱으로 설치 — 주소창 없이 열기
            </button>
            <p className="mt-1 text-xs text-slate-500 leading-snug">
              홈 화면 아이콘 하나로 <b className="text-slate-500">📦 재고 · 📅 일정</b> 두 칸을 씁니다.
              위 주소창도 사라집니다.
            </p>
          </div>
        )}
        {showInstallTip && !canInstall && !inAppShell && (
          <p className="sm:hidden w-full text-xs text-slate-500 leading-snug">
            📱 폰 홈 화면에 앱으로 두려면{' '}
            <Link to="/m/calendar" className="text-blue-600 font-medium underline">
              [앱 화면 열기]
            </Link>
          </p>
        )}
        {/* ⭐⭐⭐**단추가 없는 진짜 이유를 적는다** (v2.188.0 · 2026-09-17).
            안드로이드 크롬은 **한 주소에 앱 하나**만 깔게 하고, 이미 깔린 앱의 범위가 이 주소를
            덮으면 설치 신호를 **아예 던지지 않는다.** 「SM 포털」 앱(범위 `/`)이 있는 폰에서는
            그래서 단추가 영영 안 나온다 — 새로고침해도, 메뉴를 뒤져도 안 나온다.
            ⛔⛔**종전 문구는 「새로고침하면 나옵니다」·「메뉴에서 [앱 설치]」였는데 둘 다 거짓이었다**
              (2026-09-17 · 사용자가 그대로 해 보고 안 돼서 두 번 헛걸음했다). 그 상황에서 메뉴에
              실제로 있는 것은 [바로가기 추가]뿐이고, 그것으로 깔면 브라우저 탭이라 주소창이 남는다.
            → 할 수 있는 일(이미 있는 앱을 열어 📅 일정 칸)을 **먼저** 적는다. */}
        {showInstallTip && !canInstall && inAppShell && (
          <p className="sm:hidden w-full text-xs text-slate-500 leading-snug">
            📱 폰에 이미 <b className="text-slate-500">「SM 포털」 앱</b>이 있으면{' '}
            <b className="text-slate-500">그 앱을 열어 위 📅 일정 칸</b>을 쓰세요 — 같은 앱입니다.
            <br />
            ⚠ 안드로이드는 <b className="text-slate-500">한 주소에 앱을 하나만</b> 허용해서,
            앱이 이미 있으면 새 아이콘은 생기지 않습니다(새로고침해도 마찬가지).
            아이폰은 <b className="text-slate-500">공유 → [홈 화면에 추가]</b> 로 이 화면을 따로 둘 수 있습니다.
          </p>
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => move(-1)}
            className="btn btn-secondary btn-icon text-lg"
            aria-label="이전 달"
          >
            ‹
          </button>
          <span className="text-base font-semibold text-slate-700 min-w-[120px] text-center">
            {ym.y}년 {ym.m + 1}월
          </span>
          <button
            type="button"
            onClick={() => move(1)}
            className="btn btn-secondary btn-icon text-lg"
            aria-label="다음 달"
          >
            ›
          </button>
          <button
            type="button"
            onClick={goToday}
            disabled={isThisMonth && selected === today}
            className="btn btn-outline ml-1"
          >
            오늘
          </button>
          {canCreate ? (
            <button
              type="button"
              onClick={() => openNew(selected)}
              className="btn btn-primary ml-2"
            >
              ＋ 새 일정
            </button>
          ) : null}
        </div>
      </div>

      {q.isError ? (
        <div className="mb-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          웍스 캘린더를 불러오지 못했습니다 — {(q.error as Error).message}
        </div>
      ) : null}
      {/* ⭐휴일 표가 없는 해는 「휴일이 없는 달」과 화면에서 똑같아 보인다 — 그건 고장이므로 말해 준다.
          마지막 해에 들어서면 미리 알려 다음 해 자료를 채울 시간을 준다. */}
      {holidayTableMissing(ym.y) ? (
        <div className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {ym.y}년 공휴일 자료가 아직 없습니다 — 이 해는 휴일이 표시되지 않습니다. 관리자에게 알려 주세요.
        </div>
      ) : holidayTableEndingSoon(ym.y) ? (
        <div className="mb-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-1.5">
          공휴일 자료는 {HOLIDAY_LAST_YEAR}년까지 들어 있습니다.
        </div>
      ) : null}

      {/* ⭐연결 안 했으면 **왜 단추가 없는지** 여기서 말해 준다 (v2.187.0).
          단추만 조용히 감추면 「나는 왜 못 넣지」로 문의가 온다 —
          이 화면의 원래 원칙(막는 이유를 문장으로)과 같은 자리다. */}
      {q.data?.enabled && q.data.calendarLinked === false ? (
        <div className="mb-3 flex items-center gap-2 flex-wrap text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          <span>
            <b>보기 전용</b>입니다 — 네이버웍스 계정을 연결하면 <b>본인 명의로</b> 일정을 넣고 고칠 수 있습니다.
          </span>
          <Link
            to="/mypage"
            className="px-2.5 py-1 rounded bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
          >
            연결하러 가기
          </Link>
        </div>
      ) : null}

      <div className="flex flex-col lg:flex-row gap-4">
        {/* ── 월 달력 ── */}
        <div className="flex-1 min-w-0 bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5">
          {/* ⭐⭐**주 단위로 그린다**(v2.182.0). 종전에는 42칸을 한 격자에 늘어놓고 날마다 그 날의
              목록을 따로 그려서, 3일짜리 일정이 날마다 다른 높이에 놓여 이어 보이지 않았다.
              이제 한 주를 통째로 놓고 **일정마다 줄(lane)을 정해** 그 주 내내 같은 높이로 긋는다.
              막대는 칸 격자 **위에 겹쳐** 그린다 — 그래야 칸 경계를 넘어 한 줄로 이어진다. */}
          <div className="bg-slate-200 border border-slate-200 rounded overflow-hidden space-y-px">
            <div className={`grid ${COLS} gap-px`}>
              {WEEKDAYS.map((w, i) => (
                <div
                  key={w}
                  className={`bg-slate-50 text-center text-sm py-2.5 font-semibold ${
                    i === 0 ? 'text-rose-500' : i === 6 ? 'text-blue-500' : 'text-slate-500'
                  }`}
                >
                  {w}
                </div>
              ))}
            </div>

            {weeks.map((week, wi) => {
              const { bars, overflow, laneCount } = weekLayouts[wi]!;
              // 이 주의 막대가 다 들어갈 만큼만 칸을 키운다 — 일정이 없는 주는 기본 높이 그대로
              const need =
                WEEK_BAR_TOP
                + laneCount * LANE_H + Math.max(0, laneCount - 1) * LANE_GAP
                + (overflow.some((n) => n > 0) ? LANE_GAP + MORE_H : 0)
                + 6;
              const cellMinH = Math.max(CELL_MIN_H, need);
              return (
                <div key={`w${wi}`} className="relative">
                  <div className={`grid ${COLS} gap-px`}>
                    {week.map((key, ci) => {
                      const d = key ? Number(key.slice(8, 10)) : null;
                      const isToday = key != null && key === today;
                      const isSel = key != null && key === selected;
                      const holiday = key != null ? holidayOf(key) : null;
                      // 📅 앞뒤 달 칸(v2.277.0) — 날짜·일정은 보이되 흐리게 · 1일은 「10/1」처럼 월을 붙인다
                      const out = key != null && !grid.inMonth(key);
                      const label = out && d === 1 ? `${Number(key!.slice(5, 7))}/1` : d;
                      // ⭐칸 높이·글자는 사용자 요청(2026-09-08 「캘린더 크기를 좀 더 키워 줘.
                      //   글자크기도. 특히 세로 길이를 좀 더 늘려 줘」)으로 키운 값이다.
                      return (
                        <div
                          key={key ?? `x${wi}-${ci}`}
                          onClick={key != null ? () => setSelected(key) : undefined}
                          style={{ minHeight: cellMinH }}
                          className={`p-1.5 align-top ${
                            key == null
                              ? 'bg-slate-50/60'
                              : `${out ? 'bg-slate-50 hover:bg-slate-100' : 'bg-white hover:bg-slate-50'} cursor-pointer ${isSel ? 'ring-2 ring-inset ring-blue-400' : ''}`
                          }`}
                        >
                          {/* ⚠머리줄 높이를 **h-7 로 못 박는다** — 오늘 날짜만 동그라미(28px)라
                              칸마다 높이가 달라지면, 그 위에 겹쳐 그리는 막대가 칸마다 어긋난다. */}
                          {key != null ? (
                            <div className="flex items-center justify-between h-7">
                              {/* 공휴일은 날짜를 일요일과 같은 빨강으로 — 「쉬는 날」을 한눈에 (v2.171.0) */}
                              <span
                                className={`text-sm ${
                                  isToday
                                    ? 'inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold'
                                    : holiday || ci === 0
                                      ? (out ? 'text-rose-300' : 'text-rose-500')
                                      : ci === 6
                                        ? (out ? 'text-blue-300' : 'text-blue-500')
                                        : (out ? 'text-slate-400' : 'text-slate-500')
                                }`}
                              >
                                {label}
                              </span>
                              {/* 휴일 이름 — 칸이 좁으니 한 줄로 자른다. 전체 이름은 title 로.
                                  ⚠**폰의 토·일 칸에서는 이름을 감춘다**(v2.183.1) — 그 칸이 절반 폭이라
                                    날짜를 빼면 한 글자도 못 넣는다. 「쉬는 날」은 **빨간 날짜**가 말해 주고,
                                    이름은 그 날을 누르면 오른쪽(폰은 아래)에서 볼 수 있다. */}
                              {holiday && (
                                <span
                                  title={holiday.name}
                                  className={`ml-1 flex-1 min-w-0 truncate text-right text-xs text-rose-500 ${
                                    ci === 0 || ci === 6 ? 'hidden sm:block' : ''
                                  }`}
                                >
                                  {holiday.name}
                                </span>
                              )}
                              {canCreate ? (
                                <button
                                  type="button"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setSelected(key);
                                    openNew(key);
                                  }}
                                  title="이 날에 일정 추가"
                                  className="hidden md:block text-base leading-none text-slate-300 hover:text-blue-600 px-1"
                                >
                                  ＋
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  {/* ── 일정 막대 — 칸 격자와 **같은 7열**이라 칸 경계에 정확히 맞는다 ──
                      ⭐`pointer-events-none` 으로 칸 클릭(날짜 고르기)을 막지 않는다.
                        막대 자체만 다시 살려서, 누르면 그 일정이 시작한 날이 펼쳐지게 한다. */}
                  <div
                    className={`absolute left-0 right-0 grid ${COLS} gap-x-px gap-y-0.5 pointer-events-none`}
                    style={{ top: WEEK_BAR_TOP }}
                  >
                    {bars.map(({ item: e, lane, from, to, startsHere, endsHere }) => {
                      // ⭐**납품(주황)을 먼저 본다.** 주황은 「여기서는 못 고친다」는 신호라
                      //   휴가 빨강에 덮이면 고칠 수 있는 일정처럼 보인다.
                      const cls = isDelivery(e)
                        ? 'bg-amber-50 text-amber-800 border-amber-200'
                        : isLeave(e)
                          ? 'bg-rose-100 text-rose-900 border-rose-300'
                          : 'bg-blue-50 text-blue-800 border-blue-200';
                      // 이어지는 쪽은 모서리를 깎지 않고 칸 끝까지 붙인다 — 그래야 한 줄로 이어 보인다
                      const edge = `${startsHere ? 'ml-1 rounded-l' : ''} ${endsHere ? 'mr-1 rounded-r' : ''}`;
                      const time = !e.allDay && e.startTime ? `${e.startTime} ` : '';
                      const startKey = week[from] ?? selected;
                      // 앞뒤 달 칸에만 걸친 막대는 흐리게(v2.277.0) — 이 달 일정과 한눈에 갈리게
                      const allOut = week.slice(from, to + 1).every((k) => k != null && !grid.inMonth(k));
                      return (
                        <div
                          key={`${e.eventId}-${lane}-${from}`}
                          style={{ gridColumn: `${from + 1} / ${to + 2}`, gridRow: lane + 1 }}
                          className="min-w-0 pointer-events-auto"
                        >
                          <button
                            type="button"
                            title={e.summary}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setSelected(startKey);
                            }}
                            /* ⭐폰에서는 글자를 10px 로 줄이고 좌우 여백도 깎는다 — 같은 칸에
                                두세 자를 더 보이게 하려는 것(사용자 요청 v2.183.1).
                                ⚠큰 화면은 종전 12px 그대로. */
                            className={`block w-full text-left text-[11px] sm:text-xs leading-snug border px-1 sm:px-1.5 py-1 ${cls} ${edge} ${allOut ? 'opacity-60' : ''}`}
                          >
                            {/* ⭐**폰에서도 글자를 보여 준다** (v2.183.0 · 사용자 지적 「일정 모바일에서
                                글씨가 안보여」). v2.182.0 까지는 좁은 화면에서 색 막대만 보였다 —
                                칸 하나가 52px 이라 서너 자밖에 안 보인다는 이유였는데,
                                **여러 날 일정이 한 줄로 이어지면서 막대가 칸 폭을 넘게 되어**
                                이제 대부분 다 읽힌다(하루짜리만 잘리고, 그건 날짜를 누르면 아래에 펼쳐진다).
                                ⚠글자를 10.5px 로 줄여도 한 글자 더 보일 뿐이라 크기는 건드리지 않았다. */}
                            {/* ⭐⭐**폰에서는 시각을 붙이지 않는다** (v2.183.2 · 사용자 지적
                                「모바일에서는 시간을 붙이지 마. 시간이 앞에 붙어서 내용이 안보여」).
                                `09:00 ` 이 앞에서 다섯 자를 먹어, 정작 **무슨 일정인지**가 밀려났다.
                                시각은 그 날을 누르면 아래(큰 화면은 오른쪽)에서 그대로 보인다.
                                ⚠큰 화면은 칸이 넓어 시각이 도움이 되므로 종전대로 붙인다. */}
                            <span className="block truncate">
                              {time ? <span className="hidden sm:inline">{time}</span> : null}
                              {e.summary}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                    {/* 자리를 못 얻은 건수 — 칸마다 따로 (막대와 같은 격자의 마지막 줄) */}
                    {overflow.map((n, ci) =>
                      n > 0 ? (
                        <div
                          key={`more-${wi}-${ci}`}
                          style={{ gridColumn: `${ci + 1} / ${ci + 2}`, gridRow: MAX_LANES + 1 }}
                          className="block text-[11px] sm:text-xs text-slate-500 px-1 sm:px-1.5 truncate"
                        >
                          외 {n}건
                        </div>
                      ) : null,
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            {q.isLoading ? <span><LoadingLines /></span> : null}
            {/* 이 달에 걸친 일정만 센다 — 앞뒤 달 칸 일정까지 불러오므로(v2.277.0) 통째로 세면 부풀려진다 */}
            {!q.isLoading && !q.isError ? <span>이 달 일정 {monthEventCount}건</span> : null}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-300" />
              영업진행이 만든 납품 일정 (여기서는 못 고침)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-100 border border-rose-300" />
              연차·반차 등 휴가
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-100 border border-blue-300" />
              사람이 넣은 일정
            </span>
          </div>
        </div>

        {/* ── 고른 날 ── */}
        <div className="lg:w-[340px] shrink-0 bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-base font-semibold text-slate-700">{labelOf(selected)}</div>
            {canCreate ? (
              <button
                type="button"
                onClick={() => openNew(selected)}
                className="btn btn-outline btn-sm"
              >
                ＋ 이 날에 추가
              </button>
            ) : null}
          </div>

          {dayList.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">이 날은 일정이 없습니다.</div>
          ) : (
            <div className="space-y-2">
              {dayList.map((e, idx) => (
                <div
                  key={`${e.eventId}-${idx}`}
                  className={`border rounded p-3 ${
                    isDelivery(e)
                      ? 'border-amber-200 bg-amber-50/50'
                      : isLeave(e)
                        ? 'border-rose-300 bg-rose-50'
                        : 'border-slate-200'
                  }`}
                >
                  <div className="text-base font-medium text-slate-800 break-words">{e.summary}</div>
                  <div className="text-[13px] text-slate-500 mt-1">
                    {e.allDay
                      ? e.endDate > e.date
                        ? `종일 · ${e.date} ~ ${e.endDate}`
                        : '종일'
                      : `${e.startTime ?? ''} ~ ${e.endTime ?? ''}`}
                    {e.location ? ` · ${e.location}` : ''}
                  </div>
                  {e.description ? (
                    <div className="text-[13px] text-slate-600 mt-1.5 whitespace-pre-wrap break-words">
                      {e.description}
                    </div>
                  ) : null}
                  {e.createdByName ? (
                    <div className="text-[13px] text-slate-400 mt-1.5">포털에서 등록 · {e.createdByName}</div>
                  ) : null}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {e.canEdit ? (
                      <>
                        <button
                          type="button"
                          onClick={() => openEdit(e)}
                          className="btn btn-secondary btn-sm"
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          onClick={() => askDelete(e)}
                          disabled={deleteM.isPending}
                          className="btn btn-danger btn-sm"
                        >
                          삭제
                        </button>
                      </>
                    ) : (
                      <span className="text-[13px] text-slate-400">🔒 {e.lockReason}</span>
                    )}
                    {e.viewUrl ? (
                      <a
                        href={e.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        웍스에서 열기 ↗
                      </a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 등록·수정 창 ──
          ⚠배경을 눌러도 닫히지 않는다 — 제목·장소·메모를 적다가 빈 곳을 한 번 누르면 그동안 쓴 것이
          통째로 사라지기 때문이다. 닫기는 [취소] 로만 (검사 그물 `check:modal` 이 이 규칙을 본다). */}
      {form ? (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-md mt-10">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">
              {form.eventId ? '일정 수정' : '새 일정'}
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="ui-label block mb-1">제목 *</label>
                <input
                  type="text"
                  value={form.summary}
                  autoFocus
                  maxLength={200}
                  onChange={(ev) => setForm({ ...form, summary: ev.target.value })}
                  className="ui-input w-full"
                  placeholder="예: 월간 회의"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="ui-label block mb-1">시작일 *</label>
                  <DateInput
                    value={form.date}
                    onChange={(v) =>
                      setForm({ ...form, date: v, endDate: form.endDate < v ? v : form.endDate })
                    }
                  />
                </div>
                <div>
                  <label className="ui-label block mb-1">종료일</label>
                  <DateInput value={form.endDate} onChange={(v) => setForm({ ...form, endDate: v })} />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.allDay}
                  onChange={(ev) => setForm({ ...form, allDay: ev.target.checked })}
                />
                종일
              </label>

              {!form.allDay ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="ui-label block mb-1">시작 시각 *</label>
                    <input
                      type="time"
                      value={form.startTime}
                      onChange={(ev) => setForm({ ...form, startTime: ev.target.value })}
                      className="ui-input w-full"
                    />
                  </div>
                  <div>
                    <label className="ui-label block mb-1">종료 시각 *</label>
                    <input
                      type="time"
                      value={form.endTime}
                      onChange={(ev) => setForm({ ...form, endTime: ev.target.value })}
                      className="ui-input w-full"
                    />
                  </div>
                </div>
              ) : null}

              <div>
                <label className="ui-label block mb-1">장소</label>
                <input
                  type="text"
                  value={form.location}
                  maxLength={200}
                  onChange={(ev) => setForm({ ...form, location: ev.target.value })}
                  className="ui-input w-full"
                  placeholder="예: 본사 회의실"
                />
              </div>

              <div>
                <label className="ui-label block mb-1">메모</label>
                <textarea
                  value={form.description}
                  maxLength={2000}
                  rows={3}
                  onChange={(ev) => setForm({ ...form, description: ev.target.value })}
                  className="ui-input ui-textarea w-full"
                />
              </div>

              {formError ? (
                <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                  {formError}
                </div>
              ) : null}

              <div className="text-xs text-slate-500">
                여기서 넣은 일정은 <b>회사 웍스 캘린더</b>에 그대로 들어갑니다 — 웍스 앱에서도 보입니다.
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setForm(null)}
                disabled={saving}
                className="btn btn-secondary"
              >
                취소
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? '저장 중…' : form.eventId ? '고치기' : '넣기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
